import { useEffect, useMemo, useRef, useState } from 'react';
import { getAnalyzerRule } from '../data/analysisRules';

function hasWord(text) {
  return /\p{L}+/u.test(text);
}

function tokenize(text) {
  return text.split(/([\p{L}\p{N}]+(?:[-'][\p{L}\p{N}]+)*)/gu).filter((part) => part !== '');
}

function isWordToken(token) {
  return /^[\p{L}\p{N}]+(?:[-'][\p{L}\p{N}]+)*$/u.test(token);
}

function normalizeRootText(text) {
  return (text || '')
    .trim()
    .toLowerCase()
    .replace(/^å\s+/i, '')
    .replace(/[^\p{L}]/gu, '');
}

function formatRootEntryText(entry) {
  return entry.type === 'verb' && !/^å\s+/i.test(entry.text) ? `å ${entry.text}` : entry.text;
}

function findRootEntryMatches(token, rootEntries) {
  const normalizedToken = normalizeRootText(token);
  if (normalizedToken.length < 4) return [];

  // Составные слова: разбираем слово слева направо по известным корням.
  // Находим самый длинный корень-префикс, отрезаем его и повторяем на остатке.
  const collected = [];
  let rest = normalizedToken;
  for (let guard = 0; guard < 6 && rest.length >= 4; guard += 1) {
    const segLen = rootEntries.reduce(
      (max, entry) => (entry.root && rest.startsWith(entry.root) ? Math.max(max, entry.root.length) : max),
      0,
    );
    if (segLen === 0) break;
    rootEntries.forEach((entry) => {
      if (entry.root && rest.startsWith(entry.root) && entry.root.length === segLen) collected.push(entry);
    });
    rest = rest.slice(segLen);
  }
  rootEntries.forEach((entry) => {
    if (entry.root && normalizeRootText(entry.text) === normalizedToken) collected.push(entry);
  });

  return collected
    .map((entry) => ({
      ...entry,
      displayText: formatRootEntryText(entry),
    }))
    .sort((left, right) => left.displayText.localeCompare(right.displayText))
    .filter((entry, index, entries) => (
      entries.findIndex((item) => item.displayText === entry.displayText && item.category === entry.category) === index
    ));
}

function groupRootMatches(rootMatches) {
  return rootMatches.reduce((groups, match) => {
    const existingGroup = groups.find((group) => group.root === match.root);
    const group = existingGroup || {
      root: match.root,
      translation: match.rootTranslation || '',
      words: [],
    };

    group.words.push(match);
    if (!existingGroup) groups.push(group);

    return groups;
  }, []);
}

function getSubcategoryLabel(subcategory) {
  return (subcategory || '').split('\u001F').filter(Boolean).join(' / ');
}

function getEntryLabel(entry) {
  return [entry.category, getSubcategoryLabel(entry.subcategory)].filter(Boolean).join(' / ');
}

const FORM_LABELS = {
  infinitive: 'инфинитив',
  present: 'настоящее время',
  past: 'прошедшее время',
  perfect: 'перфект',
  imperative: 'повелит. наклонение',
  singularDefinite: 'опред. форма ед.ч.',
  pluralIndefinite: 'мн. число',
  pluralDefinite: 'опред. форма мн.ч.',
};

const TYPE_LABELS = {
  verb: 'глагол',
  noun: 'существительное',
  adjective: 'прилагательное',
  adverb: 'наречие',
};

function formatLemma(entry) {
  const text = entry.text || '';
  if (entry.gender && !/^(en|ei|et)\s+/i.test(text)) {
    return entry.gender + ' ' + text;
  }
  return text;
}

function describeWeakEntry(entry) {
  const meta = [TYPE_LABELS[entry.type] || '', FORM_LABELS[entry.formKey] || '']
    .filter(Boolean)
    .join(', ');
  const translation = (entry.translations || []).filter(Boolean).join(', ');
  return { meta, translation };
}

function getDistinctEntries(entries) {
  return (entries || []).filter((entry, index, items) => (
    items.findIndex((candidate) => (
      candidate.text === entry.text
      && candidate.category === entry.category
      && candidate.subcategory === entry.subcategory
      && candidate.type === entry.type
    )) === index
  ));
}

function findPhraseMatch(parts, startIndex, analyzerMap, maxPhraseLength) {
  if (!isWordToken(parts[startIndex])) return null;

  const words = [];

  for (let index = startIndex; index < parts.length && words.length < maxPhraseLength; index += 1) {
    if (isWordToken(parts[index])) {
      words.push(parts[index]);
      continue;
    }

    if (words.length > 0 && !/^\s+$/u.test(parts[index])) break;
  }

  for (let length = words.length; length > 0; length -= 1) {
    let seenWords = 0;
    let candidateEndIndex = startIndex;

    for (let index = startIndex; index < parts.length; index += 1) {
      if (isWordToken(parts[index])) seenWords += 1;
      candidateEndIndex = index;
      if (seenWords === length) break;
    }

    const phrase = words.slice(0, length).join(' ').toLowerCase();
    const entries = analyzerMap.__entriesByTerm?.[phrase] || (analyzerMap[phrase] ? [analyzerMap[phrase]] : []);
    const entry = entries[0];

    if (entry) {
      return {
        entry,
        entries,
        endIndex: candidateEndIndex,
        text: parts.slice(startIndex, candidateEndIndex + 1).join(''),
      };
    }
  }

  return null;
}

function computeSentenceFrequency(parts, analyzerMap, analyzerAliases) {
  const tokenSentence = new Array(parts.length).fill(0);
  const tokenIdentity = new Array(parts.length).fill(null);
  const counts = [];
  let s = 0;
  const getId = (tok) => {
    let term = tok.toLowerCase();
    if (analyzerAliases && analyzerAliases[term]) term = analyzerAliases[term].toLowerCase().replace(/\s+/g, " ");
    const norm = term;
    const entries = analyzerMap.__entriesByTerm?.[norm] || (analyzerMap[norm] ? [analyzerMap[norm]] : []);
    const lemma = entries[0]?.text;
    return (lemma ? lemma.toLowerCase() : norm).replace(/^å\s+/, '');
  };
  for (let i = 0; i < parts.length; i += 1) {
    tokenSentence[i] = s;
    const part = parts[i];
    if (isWordToken(part)) {
      const id = getId(part);
      tokenIdentity[i] = id;
      if (!counts[s]) counts[s] = new Map();
      counts[s].set(id, (counts[s].get(id) || 0) + 1);
    }
  }
  return { tokenSentence, tokenIdentity, counts };
}

function findSplitPhrases(parts, analyzerMap) {
  const splits = (analyzerMap && analyzerMap.__splitPhrases) || [];
  const info = {};
  if (!splits.length) return info;
  const wordIdx = [];
  for (let i = 0; i < parts.length; i += 1) if (isWordToken(parts[i])) wordIdx.push(i);
  const norm = (t) => (t || '').toLowerCase().replace(/^å\s+/i, '');
  for (let wi = 0; wi < wordIdx.length; wi += 1) {
    const i = wordIdx[wi];
    if (info[i]) continue;
    const tok = norm(parts[i]);
    for (let s = 0; s < splits.length; s += 1) {
      const sp = splits[s];
      const verbMatch = sp.firstForms.has(tok)
        || (tok.startsWith(sp.stem) && tok.length > sp.stem.length && tok.length - sp.stem.length <= 3);
      if (!verbMatch) continue;
      let found = false;
      for (let gap = 1; gap <= 3 && wi + gap < wordIdx.length; gap += 1) {
        const j = wordIdx[wi + gap];
        const between = parts.slice(i + 1, j).join('');
        if (/[.!?;:\u2026]/.test(between)) break;
        if (norm(parts[j]) === sp.particle && !info[j]) {
          info[i] = { entry: sp.entry, role: 'verb', partner: j };
          info[j] = { entry: sp.entry, role: 'particle', partner: i };
          found = true;
          break;
        }
      }
      if (found) break;
    }
  }
  return info;
}

function collectMatchedWords(text, analyzerMap, maxPhraseLength, analyzerAliases) {
  const parts = tokenize(text);
  const out = [];
  const seen = new Set();
  const pushEntry = (chosen) => {
    if (!chosen) return;
    const key = String(chosen.text || '').trim().toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push({ category: chosen.category, subcategory: chosen.subcategory || null, text: chosen.text });
    }
  };
  for (let index = 0; index < parts.length; index += 1) {
    if (!isWordToken(parts[index])) continue;
    if (analyzerAliases) {
      const wordIdxs = [];
      let scan = index;
      while (scan < parts.length && wordIdxs.length < 5) {
        const p = parts[scan];
        if (isWordToken(p)) { wordIdxs.push(scan); scan += 1; }
        else if (p.trim() === '') { scan += 1; }
        else break;
      }
      let aliasHit = null;
      for (let len = wordIdxs.length; len >= 1; len -= 1) {
        const slice = wordIdxs.slice(0, len);
        const key = slice.map((i) => parts[i].trim().toLowerCase()).join(' ');
        if (analyzerAliases[key]) { aliasHit = { slice, alt: analyzerAliases[key] }; break; }
      }
      if (aliasHit) {
        const altKey = aliasHit.alt.trim().toLowerCase().replace(/\s+/g, ' ');
        const aEntries = getDistinctEntries(
          analyzerMap.__entriesByTerm?.[altKey] || (analyzerMap[altKey] ? [analyzerMap[altKey]] : []),
        );
        const aBase = aEntries.filter((e) => e.isBaseMatch);
        const aPool = aBase.length > 0 ? aBase : aEntries;
        pushEntry(aPool.find((e) => e && e.isSkeleton) || aPool[0]);
        index = aliasHit.slice[aliasHit.slice.length - 1];
        continue;
      }
    }

    const match = findPhraseMatch(parts, index, analyzerMap, maxPhraseLength);
    if (match && match.entry) {
      const allEntries = getDistinctEntries(match.entries || []);
      const baseEntries = allEntries.filter((c) => c.isBaseMatch);
      const matchEntries = baseEntries.length > 0 ? baseEntries : allEntries;
      pushEntry(matchEntries.find((e) => e && e.isSkeleton) || matchEntries[0]);
      index = match.endIndex;
    }
  }
  return out;
}

function renderAnalysis(text, analyzerMap, analyzerRootEntries, analyzerMaxPhraseLength, analyzerOptions, analyzerAliases) {
  const parts = tokenize(text);
  const rendered = [];
  const freq = analyzerOptions.sizeByFrequency ? computeSentenceFrequency(parts, analyzerMap, analyzerAliases) : null;
  const splitInfo = findSplitPhrases(parts, analyzerMap);
  const sizeFor = (idx, identity) => {
    if (!freq) return null;
    const sIdx = freq.tokenSentence[idx];
    const id = identity != null ? identity : freq.tokenIdentity[idx];
    if (id == null || !freq.counts[sIdx]) return null;
    const count = freq.counts[sIdx].get(id) || 0;
    if (count <= 1) return null;
    return Math.min(1 + (count - 1) * 0.22, 2);
  };

  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];

    // Альтернатива (ручная замена): ищем самую длинную фразу-алиас (до 5 слов) из слов подряд.
    // Вариант 2: исходные слова автора заменяются на введённую альтернативу.
    if (analyzerAliases && isWordToken(part)) {
      const wordIdxs = [];
      let scan = index;
      while (scan < parts.length && wordIdxs.length < 5) {
        const p = parts[scan];
        if (isWordToken(p)) { wordIdxs.push(scan); scan += 1; }
        else if (p.trim() === '') { scan += 1; }
        else break;
      }
      let aliasHit = null;
      for (let len = wordIdxs.length; len >= 1; len -= 1) {
        const slice = wordIdxs.slice(0, len);
        const key = slice.map((i) => parts[i].trim().toLowerCase()).join(' ');
        if (analyzerAliases[key]) { aliasHit = { slice, alt: analyzerAliases[key] }; break; }
      }
      if (aliasHit) {
        const { slice, alt } = aliasHit;
        const originalPhrase = slice.map((i) => parts[i]).join(' ');

        if (analyzerOptions.showRootWords) {
          const aRootMatches = findRootEntryMatches(alt, analyzerRootEntries);
          const aRootGroups = groupRootMatches(aRootMatches);
          if (aRootGroups.length === 0) {
            rendered.push(
              <span key={`alias-root-${index}`} className="analysis-token analysis-alias" data-word={originalPhrase} data-alias="1" title="Заменено вручную (альтернатива)">
                <span className="analysis-alias-text">{alt}</span>
              </span>,
            );
          } else {
            rendered.push(
              <span key={`alias-root-${index}`} className="analysis-token colored-word has-translation root-related-token analysis-alias" data-word={originalPhrase} data-alias="1" style={{ color: aRootMatches[0].color || '#0f172a' }}>
                <span className="analysis-translation root-translation">
                  {aRootGroups.map((group) => (
                    <span className="root-translation-group" key={group.root}>
                      <span className="root-translation-row root-row">
                        <strong>{group.root}</strong>
                        {group.translation && <span>{group.translation}</span>}
                      </span>
                      {group.words.map((word) => {
                        const translations = (word.translations || []).filter(Boolean).join(', ');
                        return (
                          <span className="root-translation-row" key={`${word.displayText}-${word.category}`}>
                            <strong>{word.displayText}</strong>
                            {translations && <span>{translations}</span>}
                          </span>
                        );
                      })}
                    </span>
                  ))}
                </span>
                <span className="analysis-alias-text">{alt}</span>
              </span>,
            );
          }
          index = slice[slice.length - 1];
          continue;
        }

        const altKey = alt.trim().toLowerCase().replace(/\s+/g, ' ');
        const aEntries = getDistinctEntries(
          analyzerMap.__entriesByTerm?.[altKey] || (analyzerMap[altKey] ? [analyzerMap[altKey]] : []),
        );
        const aBase = aEntries.filter((e) => e.isBaseMatch);
        const aPool = aBase.length > 0 ? aBase : aEntries;
        const aEntry = aPool.find((e) => e && e.isSkeleton) || aPool[0];
        const aTr = aEntry ? (aEntry.translations || []).filter(Boolean).join(', ') : '';
        const aHasPhoto = Boolean(aEntry && aEntry.photo && aEntry.photo.dataUrl);
        const aHasPopup = Boolean(aTr) || aHasPhoto;
        const aScale = sizeFor(index);
        rendered.push(
          <span
            key={`alias-${index}-${slice.length}`}
            className={`analysis-token analysis-alias${aEntry ? ' colored-word analysis-linkable' : ''}${aHasPopup ? ' has-translation' : ''}`}
            data-word={originalPhrase}
            data-alias="1"
            title="Заменено вручную (альтернатива)"
            style={{ color: aEntry ? (aEntry.color || '#0f172a') : '#0f172a', ...(aScale ? { fontSize: aScale + 'em' } : {}) }}
            {...(aEntry ? { 'data-anword': aEntry.text, 'data-ancat': aEntry.category, 'data-ansub': aEntry.subcategory || '', ...(aEntry.isEvikPublic ? { 'data-evik': '1' } : {}) } : {})}
          >
            {aHasPopup ? (
              <span className="analysis-translation">
                {aHasPhoto ? (
                  <span className="analysis-tr-photo">
                    <img src={aEntry.photo.dataUrl} alt="" loading="lazy" decoding="async" style={{ objectPosition: '50% ' + (aEntry.photo.position ?? 50) + '%' }} />
                  </span>
                ) : null}
                {aTr ? <span className="analysis-tr-main">{aTr}</span> : null}
              </span>
            ) : null}
            <span className="analysis-alias-text">{alt}</span>
          </span>,
        );
        index = slice[slice.length - 1];
        continue;
      }
    }

    if (analyzerOptions.showRootWords) {
      if (!isWordToken(part)) {
        rendered.push(<span key={`${part}-${index}`}>{part}</span>);
        continue;
      }

      const rootMatches = findRootEntryMatches(part, analyzerRootEntries);
      const rootGroups = groupRootMatches(rootMatches);

      if (rootGroups.length === 0) {
        rendered.push(<span key={`${part}-${index}`}>{part}</span>);
        continue;
      }

      rendered.push(
        <span
          className="analysis-token colored-word has-translation root-related-token"
          key={`${part}-${index}`}
          style={{ color: rootMatches[0].color || '#0f172a' }}
        >
          <span className="analysis-translation root-translation">
            {rootGroups.map((group) => (
              <span className="root-translation-group" key={group.root}>
                <span className="root-translation-row root-row">
                  <strong>{group.root}</strong>
                  {group.translation && <span>{group.translation}</span>}
                </span>
                {group.words.map((word) => {
                  const translations = (word.translations || []).filter(Boolean).join(', ');

                  return (
                    <span className="root-translation-row" key={`${word.displayText}-${word.category}`}>
                      <strong>{word.displayText}</strong>
                      {translations && <span>{translations}</span>}
                    </span>
                  );
                })}
              </span>
            ))}
          </span>
          {part}
        </span>,
      );
      continue;
    }

    const match = findPhraseMatch(parts, index, analyzerMap, analyzerMaxPhraseLength);
    const allEntries = getDistinctEntries(match?.entries || []);
    const baseEntries = allEntries.filter((candidate) => candidate.isBaseMatch);
    const matchEntries = baseEntries.length > 0 ? baseEntries : allEntries;
    // Различные ВСТРОЕННЫЕ (скелетные) категории среди совпадений.
    // Особая рамка — только если слово реально в двух+ разных частях речи.
    const skeletonCats = [];
    matchEntries.forEach((e) => {
      if (e && e.isSkeleton && !skeletonCats.includes(e.category)) skeletonCats.push(e.category);
    });
    const isAmbiguous = skeletonCats.length > 1 && !analyzerOptions.sizeByFrequency;
    // Окраска: берём встроенную категорию (законную часть речи), если она есть.
    let entry;
    if (!isAmbiguous && skeletonCats.length >= 1) {
      entry = matchEntries.find((e) => e && e.isSkeleton) || matchEntries[0];
    } else {
      entry = matchEntries[0];
    }
    if (!entry) entry = match?.entry;
    const weakEntries = (!isAmbiguous && baseEntries.length > 0)
      ? allEntries.filter((candidate) => !candidate.isBaseMatch)
      : [];

    if (!entry || !hasWord(part)) {
      const sc = isWordToken(part) ? sizeFor(index) : null;
      const sp = isWordToken(part) ? splitInfo[index] : null;
      if (sp) {
        const spTr = (sp.entry.translations || []).filter(Boolean).join(', ');
        const spPhoto = (sp.entry.photo && sp.entry.photo.dataUrl) ? sp.entry.photo : null;
        const spPopup = sp.role === 'verb' && (spTr || spPhoto);
        rendered.push(
          <span
            key={`${part}-${index}`}
            data-word={part}
            className={`analysis-token colored-word${spPopup ? ' has-translation' : ''}`}
            style={{ color: sp.entry.color || '#0f172a', ...(sc ? { fontSize: sc + 'em' } : {}) }}
          >
            {spPopup ? (
              <span className="analysis-translation">
                {spPhoto && (
                  <span className="analysis-tr-photo">
                    <img src={spPhoto.dataUrl} alt="" loading="lazy" decoding="async" style={{ objectPosition: '50% ' + (spPhoto.position ?? 50) + '%' }} />
                  </span>
                )}
                <span className="analysis-tr-lemma">{sp.entry.text}</span>
                {spTr && <span className="analysis-tr-main">{spTr}</span>}
              </span>
            ) : null}
            {part}
          </span>,
        );
        continue;
      }
      rendered.push(<span key={`${part}-${index}`} className="analysis-word-plain" data-word={part} style={sc ? { fontSize: sc + 'em' } : undefined}>{part}</span>);
      continue;
    }

    const translations = (isAmbiguous ? [] : entry.translations || []).filter(Boolean);
    const translationText = translations.join(', ');
    const ambiguityNode = isAmbiguous ? (
      <span className="analysis-translation ambiguity-translation">
        {matchEntries.map((variant) => {
          const variantTranslations = (variant.translations || []).filter(Boolean).join(', ');

          return (
            <span className="ambiguity-row" key={`${variant.category}-${variant.subcategory}-${variant.text}`}>
              <strong>{getEntryLabel(variant)}</strong>
              {variantTranslations && <span>{variantTranslations}</span>}
            </span>
          );
        })}
      </span>
    ) : null;
    const hasPhoto = !isAmbiguous && Boolean(entry.photo && entry.photo.dataUrl);
    const hasLemmaArticle = !isAmbiguous && Boolean(entry && entry.gender);
    const hasPopup = !isAmbiguous && (Boolean(translationText) || weakEntries.length > 0 || hasPhoto || hasLemmaArticle);
    const translationNode = hasPopup ? (
      <span className={`analysis-translation${weakEntries.length > 0 ? ' analysis-translation-rich' : ''}`}>
        {hasPhoto && (
          <span className="analysis-tr-photo">
            <img src={entry.photo.dataUrl} alt="" loading="lazy" decoding="async" style={{ objectPosition: '50% ' + (entry.photo.position ?? 50) + '%' }} />
          </span>
        )}
        {hasLemmaArticle && <span className="analysis-tr-lemma">{formatLemma(entry)}</span>}
        {translationText && <span className="analysis-tr-main">{translationText}</span>}
        {weakEntries.length > 0 && (
          <span className="analysis-tr-also">
            {weakEntries.map((weak, weakIndex) => {
              const info = describeWeakEntry(weak);
              return (
                <span className="analysis-also-row" key={`${weak.text}-${weak.category}-${weakIndex}`}>
                  <strong>{formatLemma(weak)}{info.meta ? ` \u2014 ${info.meta}` : ''}</strong>
                  {info.translation && <span>{info.translation}</span>}
                </span>
              );
            })}
          </span>
        )}
      </span>
    ) : null;

    const matchScale = sizeFor(index, (entry.text || '').toLowerCase().replace(/^å\s+/, ''));
    const icon = isAmbiguous ? null : entry.tense && !analyzerOptions.showVerbTenseIcons ? null : entry.icon;

    if (icon) {
      rendered.push(
        <span
          className={`analysis-token with-icon analysis-linkable ${hasPopup ? 'has-translation' : ''}`}
          key={`${match.text}-${index}`}
          data-word={match.text}
          data-anword={entry.text}
          data-ancat={entry.category}
          data-ansub={entry.subcategory || ''}
          {...(entry.isEvikPublic ? { 'data-evik': '1' } : {})}
        >
          {translationNode}
          <span className="analysis-icon">{icon.symbol}</span>
          <span style={{ color: entry.color || '#0f172a', ...(matchScale ? { fontSize: matchScale + 'em' } : {}) }}>{match.text}</span>
        </span>,
      );
    } else {
      rendered.push(
        <span
          className={[
            'analysis-token',
            'colored-word',
            isAmbiguous ? 'ambiguous-token has-translation' : 'analysis-linkable',
            hasPopup ? 'has-translation' : '',
          ].filter(Boolean).join(' ')}
          key={`${match.text}-${index}`}
          data-word={match.text}
          style={{ color: isAmbiguous ? '#0f172a' : entry.color || '#0f172a', ...(matchScale ? { fontSize: matchScale + 'em' } : {}) }}
          {...(!isAmbiguous ? { 'data-anword': entry.text, 'data-ancat': entry.category, 'data-ansub': entry.subcategory || '', ...(entry.isEvikPublic ? { 'data-evik': '1' } : {}) } : {})}
        >
          {ambiguityNode}
          {translationNode}
          {match.text}
        </span>,
      );
    }

    index = match.endIndex;
  }

  return rendered;
}

export default function TextAnalyzer({
  text,
  analyzerMap,
  analyzerRootEntries = [],
  analyzerMaxPhraseLength,
  savedTexts = [],
  maxChars = 5000,
  nextSaveNumber,
  onTextChange,
  onSaveText,
  canListenText = false,
  onOpenInDictionary,
  onShowEvikWord,
  onCreateDeckFromText,
  analyzerAliases,
  onSetAlias,
  onRemoveAlias,
}) {
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const analysisFieldRef = useRef(null);
  const ttsAudioRef = useRef(null);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [devModal, setDevModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [savedTextMenu, setSavedTextMenu] = useState(null);

  const [aliasMenu, setAliasMenu] = useState(null);
  const aliasLongPressRef = useRef(null);
  const aliasSuppressClickRef = useRef(false);

  const getSelectionPhrase = () => {
    const sel = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection() : null;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const field = analysisFieldRef.current;
    if (!field) return null;
    let range;
    try { range = sel.getRangeAt(0); } catch (_) { return null; }
    const picked = [];
    field.querySelectorAll('[data-word]').forEach((tok) => {
      try { if (range.intersectsNode(tok)) picked.push(tok.getAttribute('data-word') || ''); } catch (_) {}
    });
    const phrase = picked.join(' ').trim().replace(/\s+/g, ' ');
    if (!phrase || phrase.indexOf(' ') === -1) return null;
    return phrase;
  };
  const openAliasMenu = (rawWord, x, y) => {
    const word = String(rawWord || '').trim().replace(/\s+/g, ' ');
    if (!word) return;
    const key = word.toLowerCase();
    const existing = (analyzerAliases && analyzerAliases[key]) || '';
    setAliasMenu({ x, y, key, original: word, value: existing, existing: Boolean(existing), error: '' });
  };
  const openAliasMenuError = (x, y, message) => {
    setAliasMenu({ x, y, error: message });
  };
  const resolveAliasTarget = (e, x, y) => {
    const phrase = getSelectionPhrase();
    if (phrase) {
      const words = phrase.split(' ').filter(Boolean);
      if (words.length > 5) { openAliasMenuError(x, y, 'Можно объединить не более 5 слов.'); return true; }
      openAliasMenu(phrase, x, y);
      return true;
    }
    const el = e.target && e.target.closest && e.target.closest('[data-word]');
    if (el) { openAliasMenu(el.getAttribute('data-word'), x, y); return true; }
    return false;
  };
  const handleAnalysisContextMenu = (e) => {
    const phrase = getSelectionPhrase();
    const el = e.target.closest && e.target.closest('[data-word]');
    if (!phrase && !el) return;
    e.preventDefault();
    e.stopPropagation();
    resolveAliasTarget(e, e.clientX, e.clientY);
  };
  const handleAnalysisTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    const x = t ? t.clientX : 0;
    const y = t ? t.clientY : 0;
    if (aliasLongPressRef.current) clearTimeout(aliasLongPressRef.current);
    aliasLongPressRef.current = setTimeout(() => {
      const hit = resolveAliasTarget(e, x, y);
      if (hit) aliasSuppressClickRef.current = true;
    }, 500);
  };
  const cancelAliasLongPress = () => {
    if (aliasLongPressRef.current) { clearTimeout(aliasLongPressRef.current); aliasLongPressRef.current = null; }
  };
  const confirmAlias = () => {
    if (!aliasMenu || aliasMenu.error || !aliasMenu.key) { setAliasMenu(null); return; }
    const v = String(aliasMenu.value || '').trim().slice(0, 50);
    if (v) { if (onSetAlias) onSetAlias(aliasMenu.key, v); }
    else if (aliasMenu.existing && onRemoveAlias) { onRemoveAlias(aliasMenu.key); }
    setAliasMenu(null);
  };
  const deleteAlias = () => {
    if (!aliasMenu) return;
    if (onRemoveAlias) onRemoveAlias(aliasMenu.key);
    setAliasMenu(null);
  };

  const [activeSavedTextId, setActiveSavedTextId] = useState(null);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [analyzerOptions, setAnalyzerOptions] = useState({
    showVerbTenseIcons: false,
    showRootWords: false,
    sizeByFrequency: false,
  });
  const analyzerRule = getAnalyzerRule();
  const canAnalyze = hasWord(text);

  const speakAnalyzerText = async () => {
    const t = (text || '').trim();
    if (!t) return;
    if (!canListenText) { setSubModal(true); return; }
    if (ttsAudioRef.current) {
      try { ttsAudioRef.current.pause(); } catch (e) { /* noop */ }
      ttsAudioRef.current = null;
      setTtsBusy(false);
      return;
    }
    setTtsBusy(true);
    try {
      const resp = await fetch('/tts/synth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ text: t }).toString(),
      });
      if (!resp.ok) throw new Error('tts_failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      ttsAudioRef.current = audio;
      audio.onended = () => { ttsAudioRef.current = null; setTtsBusy(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { ttsAudioRef.current = null; setTtsBusy(false); };
      await audio.play();
    } catch (e) {
      ttsAudioRef.current = null;
      setTtsBusy(false);
    }
  };
  const activeSavedText = savedTexts.find((savedText) => savedText.id === activeSavedTextId);
  const hasUnsavedSavedTextChanges = Boolean(
    activeSavedTextId
    && activeSavedText
    && text.trim()
    && activeSavedText.text !== text,
  );
  const canSaveText = Boolean(
    !isAnalyzed
    && text.trim()
    && (!activeSavedTextId || hasUnsavedSavedTextChanges),
  );
  const renderedAnalysis = useMemo(
    () => renderAnalysis(text, analyzerMap, analyzerRootEntries, analyzerMaxPhraseLength, analyzerOptions, analyzerAliases),
    [text, analyzerMap, analyzerRootEntries, analyzerMaxPhraseLength, analyzerOptions, analyzerAliases],
  );

  const toggleAnalyzerOption = (option) => {
    setAnalyzerOptions((currentOptions) => {
      if (option === 'sizeByFrequency') {
        return { ...currentOptions, sizeByFrequency: !currentOptions.sizeByFrequency };
      }
      return {
        ...currentOptions,
        ...(option === 'showRootWords' && !currentOptions.showRootWords
          ? { showVerbTenseIcons: false }
          : {}),
        ...(option !== 'showRootWords' && !currentOptions[option]
          ? { showRootWords: false }
          : {}),
        [option]: !currentOptions[option],
      };
    });
  };

  useEffect(() => {
    const closeMenu = () => setSavedTextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeMenu();
    };

    window.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, []);


  useEffect(() => {
    const container = analysisFieldRef.current;
    if (!container || !isAnalyzed) return undefined;
    const adjustAll = () => {
      const margin = 8;
      const vw = window.innerWidth;
      const tokens = container.querySelectorAll('.analysis-token.has-translation');
      tokens.forEach((token) => {
        const popup = token.querySelector('.analysis-translation');
        if (!popup) return;
        const tokenRect = token.getBoundingClientRect();
        const center = tokenRect.left + tokenRect.width / 2;
        const w = popup.offsetWidth;
        const leftEdge = center - w / 2;
        const rightEdge = center + w / 2;
        let shift = 0;
        if (leftEdge < margin) shift = margin - leftEdge;
        else if (rightEdge > vw - margin) shift = (vw - margin) - rightEdge;
        popup.style.setProperty('--popup-shift', Math.round(shift) + 'px');
      });
    };
    const raf = window.requestAnimationFrame(adjustAll);
    window.addEventListener('resize', adjustAll);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', adjustAll);
    };
  }, [isAnalyzed, text]);

  const runAnalysis = () => {
    if (!canAnalyze) return;
    setSaveStatus('');
    setSavedTextMenu(null);
    setIsAnalyzed(true);
  };

  const editText = () => {
    setSaveStatus('');
    setSavedTextMenu(null);
    setIsAnalyzed(false);
  };

  const resetToSaved = () => {
    if (activeSavedTextId && activeSavedText) {
      onTextChange(activeSavedText.text);
    }
    setSaveStatus('');
    setSavedTextMenu(null);
    setIsAnalyzed(false);
  };

  const labelOf = (entry) => (entry && (entry.name && entry.name.trim() ? entry.name : entry.id)) || '';

  const saveText = () => {
    if (!onSaveText) return;
    if (activeSavedTextId) {
      const savedEntry = onSaveText(text, activeSavedTextId);
      if (!savedEntry) return;
      setActiveSavedTextId(savedEntry.id);
      setSaveStatus(`Сохранено ${labelOf(savedEntry)}`);
      return;
    }
    setNameInput('');
    setNameModalOpen(true);
  };

  const confirmSaveName = () => {
    if (!onSaveText) return;
    const savedEntry = onSaveText(text, null, nameInput.slice(0, 5));
    setNameModalOpen(false);
    if (!savedEntry) return;
    setActiveSavedTextId(savedEntry.id);
    setSaveStatus(`Сохранено как ${labelOf(savedEntry)}`);
  };

  const openSavedTextMenu = (event) => {
    if (isAnalyzed || text.trim() || savedTexts.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    setSavedTextMenu({
      x: event.clientX,
      y: event.clientY,
    });
  };


  const openSavedTextMenuFromButton = (event) => {
    if (isAnalyzed || text.trim() || savedTexts.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setSavedTextMenu({ x: Math.max(8, rect.right - 200), y: rect.bottom + 4 });
  };
  const selectSavedText = (savedText) => {
    onTextChange(savedText.text);
    setActiveSavedTextId(savedText.id);
    setSaveStatus('');
    setIsAnalyzed(false);
    setSavedTextMenu(null);
  };

  const pasteFromClipboard = async () => {
    setSavedTextMenu(null);
    try {
      const clip = await navigator.clipboard.readText();
      if (clip && clip.trim()) {
        onTextChange(clip.slice(0, maxChars));
        setActiveSavedTextId(null);
        setSaveStatus('');
        setIsAnalyzed(false);
      }
    } catch (e) { /* clipboard unavailable */ }
  };

  const changeText = (value) => {
    onTextChange(value);
    setSaveStatus('');
    if (!value.trim()) {
      setActiveSavedTextId(null);
      return;
    }
  };

  const clearText = () => {
    onTextChange('');
    setActiveSavedTextId(null);
    setSaveStatus('');
    setSavedTextMenu(null);
    setIsAnalyzed(false);
  };

  return (
    <section className="text-analyzer">
      <label htmlFor="text-analyzer-input">
        Анализатор текста <span>до {maxChars.toLocaleString('ru-RU')} символов</span>
      </label>

      <div className="analyzer-options" aria-label="Опции анализатора">
        <label className="analyzer-option">
          <input
            type="checkbox"
            checked={analyzerOptions.showRootWords}
            onChange={() => toggleAnalyzerOption('showRootWords')}
          />
          <span>Однокоренные</span>
        </label>
        <label className="analyzer-option">
          <input
            type="checkbox"
            checked={analyzerOptions.sizeByFrequency}
            onChange={() => toggleAnalyzerOption('sizeByFrequency')}
          />
          <span>Повторы</span>
        </label>
      </div>

      <div className="analyzer-field" onContextMenu={openSavedTextMenu}>
        {!isAnalyzed && !text.trim() && savedTexts.length > 0 && (
          <button
            type="button"
            className="saved-texts-button"
            onClick={openSavedTextMenuFromButton}
            aria-label="Сохранённые тексты"
            title="Сохранённые тексты"
          >
            📋
          </button>
        )}
        {isAnalyzed ? (
          <div className="analysis-in-field" ref={analysisFieldRef} onContextMenu={handleAnalysisContextMenu} onTouchStart={handleAnalysisTouchStart} onTouchEnd={cancelAliasLongPress} onTouchMove={cancelAliasLongPress} onClick={(e) => {
            if (aliasSuppressClickRef.current) { aliasSuppressClickRef.current = false; return; }
            const el = e.target.closest && e.target.closest('[data-anword]');
            if (el && el.getAttribute('data-evik') === '1') {
              if (onShowEvikWord) onShowEvikWord(el.getAttribute('data-anword'));
            } else if (el && onOpenInDictionary) {
              onOpenInDictionary({
                category: el.getAttribute('data-ancat'),
                subcategory: el.getAttribute('data-ansub') || null,
                word: el.getAttribute('data-anword'),
              });
            }
          }}>
            {renderedAnalysis}
          </div>
        ) : (
          <textarea
            id="text-analyzer-input"
            maxLength={maxChars}
            value={text}
            onChange={(event) => changeText(event.target.value)}
            spellCheck="false"
          />
        )}
        {!isAnalyzed && (
          <div className="analyzer-counter">Внесено: {text.length} / Осталось: {maxChars - text.length}</div>
        )}

        {aliasMenu && (
          <div className="analyzer-alias-overlay" onClick={() => setAliasMenu(null)} onContextMenu={(e) => { e.preventDefault(); setAliasMenu(null); }}>
            <div
              className="analyzer-alias-menu"
              style={{ left: aliasMenu.x, top: aliasMenu.y }}
              onClick={(e) => e.stopPropagation()}
            >
              {aliasMenu.error ? (
                <>
                  <div className="analyzer-alias-title">{aliasMenu.error}</div>
                  <div className="analyzer-alias-buttons">
                    <button type="button" className="analyzer-alias-cancel" onClick={() => setAliasMenu(null)}>Закрыть</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="analyzer-alias-title">Альтернатива для «{aliasMenu.original}»</div>
                  <input
                    className="analyzer-alias-input"
                    type="text"
                    maxLength={50}
                    autoFocus
                    value={aliasMenu.value}
                    placeholder="Правильное слово или фраза"
                    onChange={(e) => setAliasMenu((m) => (m ? { ...m, value: e.target.value } : m))}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmAlias(); if (e.key === 'Escape') setAliasMenu(null); }}
                  />
                  <div className="analyzer-alias-buttons">
                    <button type="button" className="analyzer-alias-ok" onClick={confirmAlias}>ОК</button>
                    {aliasMenu.existing && (
                      <button type="button" className="analyzer-alias-del" onClick={deleteAlias}>Удалить</button>
                    )}
                    <button type="button" className="analyzer-alias-cancel" onClick={() => setAliasMenu(null)}>Отмена</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {savedTextMenu && (
          <div
            className="saved-text-context-menu"
            style={{ left: savedTextMenu.x, top: savedTextMenu.y }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="saved-text-menu-title">Сохраненные тексты</div>
            <div className="saved-text-menu-list">
              <button type="button" className="saved-text-paste" onClick={pasteFromClipboard}>Вставить</button>
              {savedTexts.map((savedText) => (
                <button
                  key={`${savedText.id}-${savedText.createdAt}`}
                  type="button"
                  onClick={() => selectSavedText(savedText)}
                >
                  {labelOf(savedText)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="analyzer-actions">
        <button
          className="btn-std analyzer-button"
          type="button"
          disabled={!canAnalyze}
          onClick={runAnalysis}
          title={analyzerRule.title}
        >
          Анализировать
        </button>

        {(isAnalyzed || hasUnsavedSavedTextChanges) && (
          <button
            className="btn-std analyzer-button"
            type="button"
            onClick={isAnalyzed ? editText : resetToSaved}
          >
            Отменить
          </button>
        )}

        {!isAnalyzed && text.trim() && (
          <button
            className="btn-std analyzer-button"
            type="button"
            onClick={clearText}
          >
            Очистить
          </button>
        )}

        {canAnalyze && onCreateDeckFromText && (
          <button
            className="btn-std analyzer-button"
            type="button"
            onClick={() => onCreateDeckFromText(collectMatchedWords(text, analyzerMap, analyzerMaxPhraseLength, analyzerAliases))}
            title="Собрать слова из текста в отдельную колоду для изучения"
          >
            📚 В колоду
          </button>
        )}

        {canAnalyze && (
          <button
            className="btn-std analyzer-button"
            type="button"
            onClick={speakAnalyzerText}
            title="Прослушать текст норвежским голосом"
          >
            {ttsBusy ? '⏸ Остановить' : '🔊 Прослушать'}
          </button>
        )}

        {canSaveText && (
          <div className="save-analysis-group">
            {saveStatus && <span className="save-status-text">{saveStatus}</span>}
            <button
              className="save-disk-button"
              type="button"
              onClick={saveText}
              title={activeSavedTextId ? "Сохранить изменения" : `Сохранить текст ${nextSaveNumber}`}
              aria-label="Сохранить текст"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
      </div>
      {subModal && (
        <div className="subcat-delete-overlay" onClick={() => setSubModal(false)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <div className="subcat-delete-title">Прослушивание ограничено</div>
            <div className="subcat-delete-sub">Чтобы прослушивать текстовые файлы вам необходимо оформить платную подписку на Norsk Pro.</div>
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" onClick={() => setSubModal(false)}>Отмена</button>
              <button type="button" className="subcat-row-confirm" onClick={() => { setSubModal(false); setDevModal(true); }}>Оформить</button>
            </div>
          </div>
        </div>
      )}
      {devModal && (
        <div className="subcat-delete-overlay" onClick={() => setDevModal(false)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <div className="subcat-delete-title">Модуль в разработке</div>
            <div className="subcat-delete-sub">Данный модуль находится в разработке, мы сообщим вам когда платная подписка заработает.</div>
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-row-confirm" onClick={() => setDevModal(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
      {nameModalOpen && (
        <div className="subcat-delete-overlay" onClick={() => setNameModalOpen(false)}>
          <div className="subcat-delete-card" onClick={(e) => e.stopPropagation()}>
            <div className="subcat-delete-title">Имя текста</div>
            <div className="subcat-delete-sub">Короткое имя (до 5 символов). Покажется вместо номера. Можно оставить пустым.</div>
            <input
              type="text"
              className="analyzer-name-input"
              maxLength={5}
              value={nameInput}
              autoFocus
              placeholder="напр. дом"
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveName(); }}
            />
            <div className="subcat-delete-actions">
              <button type="button" className="subcat-delete-cancel" onClick={() => setNameModalOpen(false)}>Отмена</button>
              <button type="button" className="subcat-row-confirm" onClick={confirmSaveName}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
